import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import chipStyld from 'components/Chip/styles';
import fieldStyld from 'components/Field/styles';
import { FIELD_MAX_HEIGHT, ELEMENT_MAX_WIDTH } from 'styles';
import { DEEP_BORDER_BOX } from 'common';

const {
  SPACING,
  TYPOGRAPHY,
  RADIUS,
  COLORS,
  FONT_SIZE,
} = Tokens;

const GET_BORDER_COLOR = props =>
  props.disabled ? props.theme.disabled.border
    : props.error ? props.theme.error.border
      : props.hovered ? props.theme.hover.border
        : props.focused ? props.theme.focused.border
          : props.theme.border;

const GET_OUTLINED_FOCUSED_STYLE = props => `inset 0 0 0 ${SPACING.SPACING_2} ${GET_BORDER_COLOR(props)}`;
const GET_UNDERLINED_FOCUSED_STYLE = props => `inset 0 -${SPACING.SPACING_2} 0 0 ${GET_BORDER_COLOR(props)}`;
const GET_FOCUSED_BORDER_STYLE = props => props.variant === 'outlined'
  ? GET_OUTLINED_FOCUSED_STYLE(props)
  : GET_UNDERLINED_FOCUSED_STYLE(props);

const GET_OUTLINED_HOVER_STYLE = props => `inset 0 0 0 ${SPACING.SPACING_2} ${GET_BORDER_COLOR(props)}`;
const GET_UNDERLINED_HOVER_STYLE = props => `inset 0 -${SPACING.SPACING_2} 0 0 ${GET_BORDER_COLOR(props)}`;
const GET_HOVER_BORDER_STYLE = props => props.variant === 'outlined'
  ? GET_OUTLINED_HOVER_STYLE(props)
  : GET_UNDERLINED_HOVER_STYLE(props);

const SELECTABLE_TEXT = css`
  -webkit-touch-callout: text;
  -webkit-user-select: text;
  -khtml-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
  & * {
    -webkit-touch-callout: text;
    -webkit-user-select: text;
    -khtml-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    user-select: text;
  }`;
export default {
  TextwChipsCore: styled.div`
    ${SELECTABLE_TEXT}
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    position: relative;
    border-radius: ${({ variant }) => variant === 'outlined' ? `${RADIUS.RADIUS_2}` : '0'};
    width: 100%;
    display: flex;
    ${TYPOGRAPHY.BODY};
    ${({ multiline }) => multiline && css`
      /* textarea like */
      flex-wrap: wrap;
      flex: initial;
      min-height: 2rem;
      overflow-x: hidden;
      overflow-y: auto;
      padding: ${SPACING.SPACING_8};
      ${props => props.variant === 'underlined' && css`padding: ${SPACING.SPACING_8} 0;`}
      ${({ hasClearButton }) => hasClearButton && css`
        ${props => css`padding-right: ${!(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_24}` : `${SPACING.SPACING_8}`};`}
        ${props => css`padding-left: ${(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_24}` : `${SPACING.SPACING_8}`};`}
      `};
      ${props => props.readOnly && css`padding: ${SPACING.SPACING_8} 0;`}
      line-height: 1.313rem;
      /* end textarea like*/
    `};
    ${({ multiline }) => !multiline && css`
      /* textinput like */
      max-width: 100%;
      align-items: flex-start;
      height: 2rem;
      padding: 0 ${SPACING.SPACING_8};
      input {
        ${TYPOGRAPHY.BODY}; // for TextwChipsInput text
      }
      ${props => props.variant === 'underlined' && css`padding: ${SPACING.SPACING_8} 0;`}
      ${props => props.variant === 'underlined' && 'padding: 0;'}
      ${props => props.variant === 'outlined' && props.hasClearButton && props.RTL && `padding: 0 ${SPACING.SPACING_8} 0 0;`}
      ${props => props.variant === 'outlined' && props.hasClearButton && !props.RTL && `padding: 0 0 0 ${SPACING.SPACING_8};`}
      ${props => props.readOnly && css`padding: ${SPACING.SPACING_8} 0;`}
      input {
        &::placeholder {
          color: ${props => props.theme.placeholder};
        }
      }
      /* end textinput like */
    `};
    color: ${props => props.theme.content};
    ${props => css`
      box-shadow: ${props.readOnly
    ? 'none'
    : props.variant === 'outlined'
      ? `inset 0 0 0 0.0625rem ${GET_BORDER_COLOR(props)}`
      : `inset 0 -0.0625rem 0 0 ${GET_BORDER_COLOR(props)}`};
      flex: none;
    `}
    border: none;
    background-color: ${props => props.theme.background};
    ${({ focused }) => focused && css`
      ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_FOCUSED_BORDER_STYLE({ ...props, focused: true })};`}}
      outline: none;
    `};
    &:hover {
      &::placeholder {
        color: ${props => props.theme.hover.placeholder}
      }
      ${({ disabled }) => !disabled && css`
        ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_HOVER_BORDER_STYLE({ ...props, hovered: true })};`}
        outline: none;
      `};
    }
    &:hover:focus {
      ${props => css`box-shadow: ${props.readOnly ? 'none' : GET_FOCUSED_BORDER_STYLE({ ...props, focused: true })};`}
      outline: none;
    }
    &:disabled {
      background-color: ${props => props.theme.disabled.background};
      color: ${props => props.theme.disabled.content};
      ${props => props.variant === 'outlined' && css`box-shadow: ${props.readOnly ? 'none' : GET_FOCUSED_BORDER_STYLE({ ...props, disabled: true })};`}
      ${props => props.variant === 'underlined' && css`box-shadow: none; border-bottom: ${props.readOnly ? 'none' : `0.0625rem dotted ${GET_BORDER_COLOR(props)}`};`}
      &::placeholder {
        color: ${props => props.theme.disabled.placeholder};
      }
    }
    ${({ disabled }) => disabled && css`
      background-color: ${props => props.theme.disabled.background};
    `};
    ${chipStyld.ChipLabel} {
      span {
        font-size: ${FONT_SIZE.FONT_SIZE_14}; // designer wants Chip font-size to match the field font-size
      }
    }
    ${chipStyld.Label} {
      user-select: text;
    }
    ${fieldStyld.Field} {
      ${props => css`padding-right: ${!(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_4}` : 0};`}
      ${props => css`padding-left: ${(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_4}` : 0};`}
      border: none;
      ${TYPOGRAPHY.BODY}
    }
    /* custom resizer for non-FF browsers */
    ::-webkit-resizer {
      display: none;   
    }
  `,
  ChipEditWrapper: styled.input`
    display: inline-block;
    outline: none;
    border:  none;
    direction: ltr;
    background-color: ${COLORS.KINGFISHER_BLUE_10};
    border-radius: 0.875rem;
    ${props => css`padding: ${(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_8} 0` : `0 ${SPACING.SPACING_8}`};`}
    height: 1.25rem
  `,
  WholeWrapper: styled.div`
    display: flex;
    flex-direction: column;
    ${props => props.maxWidth ? css`
      max-width: ${ELEMENT_MAX_WIDTH};
    ` : css`
      max-width: 100%;
    `}
    ${DEEP_BORDER_BOX}
  `,
  ContentWrapper: styled.div`
    ${SELECTABLE_TEXT}
    ${({ multiline }) => !multiline && css`
      ${props => css`flex-direction: ${(props.RTL || props.$rtl || props.rtl) ? 'row-reverse' : 'row'};`}
      display: inline-flex;
      align-items: center;
      overflow: hidden;
      align-self: center;
    `};
    ${({ multiline }) => multiline && css`
      ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
      display: block;
    `};
    max-width: 100%;
    &:focus {
      outline: none;
    }
  `,
  MetaContainer: styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    max-height: ${FIELD_MAX_HEIGHT};
    max-width: 100%;
    margin-top: ${SPACING.SPACING_4};
    ${({ fieldWidth }) => css`
      ${fieldWidth ? `width:${fieldWidth};` : 'width: 100%;'}
      ${fieldWidth === 0 ? 'display: none;' : 'display: flex;'}
    `}
  `,
  EditableWrapper: styled.div`
    display: inline;
    min-height: 100%;
    span {
      ${TYPOGRAPHY.BODY}; // for ContentEditable text
      ${SELECTABLE_TEXT}
      color: ${props => props.theme.content};
      ${({ $isplaceholder }) => $isplaceholder && css`
        color: ${props => props.theme.placeholder}
      `};
      ${({ disabled }) => disabled && css`
        color: ${props => props.theme.disabled.content};
        ${({ $isplaceholder }) => $isplaceholder && css`
          color: ${props => props.theme.disabled.placeholder}
        `};
      `}
      &:focus {
        outline: none;
      }
      &:focus-visible {
        outline: none;
      }
    }
  `,
};